import { createSlice } from '@reduxjs/toolkit';
import dayjs from 'dayjs';

// Randomize data for dynamic UI testing
const DAILY_USAGE = parseFloat((Math.random() * (50.0 - 5.0) + 5.0).toFixed(1)); // 5.0 ~ 50.0
const isZeroBalance = Math.random() < 0.3; // 30% chance of zero balance
const randomRemainingDays = isZeroBalance ? 0 : Math.floor(Math.random() * (365 - 30) + 30); // 30 ~ 365 days
const calculatedBalance = Math.floor(randomRemainingDays * DAILY_USAGE);

const initialState = {
  licensePreConfirm: {
    data: [
      {
        siteId: "taipei-uuid",
        siteName: "Taipei",
        service: ["healthAI"],
        action: "add",
        deviceCount: 1,
        deviceTotalCount: 9,
        rate: "1 credit/day/device",
        dailyRate: 1,
        remainingDays: 30,
        sufficientCredits: true,
      }
    ]
  },
  dashboard: {
    data: {
      balance: calculatedBalance,
      dailyUsage: DAILY_USAGE,
      remainingDays: randomRemainingDays,
      nextDeduction: Math.floor(DAILY_USAGE * 30),
      nextDeductionAt: dayjs.utc().add(1, 'month').startOf('month').unix()
    }
  },
  serviceStatus: {
    data: {
      msp: {
        enabled: false,
        name: "",
        walletBalance: 0
      },
      org: {
        name: "Org Test",
        underMsp: false,
        walletBalance: calculatedBalance,
        aiChatbotEnabled: true,
        nmsMspEnabled: false,
        sites: [
          {
            id: "taipei-uuid",
            name: "Taipei",
            accessPoints: 5,
            switches: 3,
            services: {
              nmsPro: false,
              healthAI: true
            }
          },
          {
            id: "kaohsiung-uuid",
            name: "Kaohsiung",
            accessPoints: 12,
            switches: 4,
            services: {
              nmsPro: true,
              healthAI: false
            }
          }
        ]
      }
    }
  },
  siteServiceStatus: {
    data: {
      name: "New Site1234",
      accessPoints: 5,
      switches: 2,
      services: {
        nmsPro: true,
        healthAI: false
      }
    }
  },
  subscriptions: {
    data: {
      sites: [
        { id: "taipei-uuid", name: "Taipei" },
        { id: "kaohsiung-uuid", name: "Kaohsiung" }
      ],
      orgLevel: [
        { id: 'ai-chatbot', name: 'AI Chatbot', status: 'Active', price: '1 Credit/day', scope: 'Organization-wide' },
        { id: 'nms-msp', name: 'NMS MSP', status: 'Inactive', price: '10 Credits/day', scope: 'Organization-wide' }
      ],
      siteLevel: [
        { id: 'health-ai', name: 'Health AI Insight', price: '1 Credit/device/day' },
        { id: 'nms-pro', name: 'NMS Pro', price: '1 Credit/device/day' }
      ],
      siteSubscriptions: {
        'taipei-uuid': { 'health-ai': 'Active', 'nms-pro': 'Inactive' },
        'kaohsiung-uuid': { 'health-ai': 'Inactive', 'nms-pro': 'Active' }
      }
    }
  },
  baseRates: {
    data: {
      msp: {
        base: 15,
        nmsPro: 1,
        healthAI: 1
      },
      nonMsp: {
        base: 0,
        nmsPro: 1,
        healthAI: 1
      },
      aiChatbot: 1
    }
  },
  deviceCounts: {
    data: {
      "taipei-uuid": 8,
      "kaohsiung-uuid": 16
    }
  },
  history: {
    data: [
      {
        timestamp: dayjs('2026-04-29').unix(),
        type: "Deposit",
        descriptionParams: { Account: "test123@gmail.com", Key: "LIC-2024-AB001" },
        change: 5000,
        balance: calculatedBalance
      },
      {
        timestamp: dayjs('2026-04-28').unix(),
        type: "MonthlyCharge",
        descriptionParams: { Site: "TEST", Devices: 10, Days: 30, Service: "Health AI Insight" },
        change: -1250,
        balance: calculatedBalance - 5000
      },
      {
        timestamp: dayjs('2026-04-15').unix(),
        type: "AddDevice",
        descriptionParams: { Site: "TEST", Devices: 5, Days: 10, Service: "NMS Pro" },
        change: -350,
        balance: calculatedBalance - 6250
      },
      {
        timestamp: dayjs('2026-04-10').unix(),
        type: "Deposit",
        descriptionParams: { Account: "system@newhope.com", Key: "REFUND-2024-001" },
        change: 200,
        balance: calculatedBalance - 6600
      },
      {
        timestamp: dayjs('2026-04-01').unix(),
        type: "MonthlyCharge",
        descriptionParams: { Site: "TEST", Devices: 10, Days: 30, Service: "Health AI Insight" },
        change: -1250,
        balance: calculatedBalance - 6400
      },
      {
        timestamp: dayjs('2026-03-28').unix(),
        type: "AddDevice",
        descriptionParams: { Site: "TEST", Devices: 5, Days: 10, Service: "Health AI" },
        change: -125,
        balance: calculatedBalance - 7650
      },
      {
        timestamp: dayjs('2026-03-25').unix(),
        type: "Deposit",
        descriptionParams: { Account: "test123@gmail.com", Key: "LIC-2024-AB002" },
        change: 10000,
        balance: calculatedBalance - 7775
      },
      {
        timestamp: dayjs('2026-03-15').unix(),
        type: "MonthlyCharge",
        descriptionParams: { Site: "TEST", Devices: 9, Days: 30, Service: "Health AI Insight" },
        change: -1100,
        balance: calculatedBalance - 17775
      },
      {
        timestamp: dayjs('2026-03-10').unix(),
        type: "RemoveDevice",
        descriptionParams: { Site: "TEST", Device: 5, Days: 5, Service: "NMS Pro" },
        change: 280,
        balance: calculatedBalance - 18875
      },
      {
        timestamp: dayjs('2026-03-01').unix(),
        type: "MonthlyCharge",
        descriptionParams: { Site: "TEST", Devices: 9, Days: 30, Service: "Health AI Insight" },
        change: -900,
        balance: calculatedBalance - 18595
      }
    ]
  },
  subscribeResult: {
    data: {
      status: "success"
    }
  },
  import: {
    data: Array.from({ length: 50 }, (_, i) => {
      const keys = ["H7K9", "W2NP", "R4X6", "M1VB", "L8QS", "G3F5", "T7Y1", "A9Z4", "E2D8", "C6K0", "V2J6", "T9F4", "C1G8", "Z5D3", "S7H1", "X4R9", "L2N6", "Q8K1", "W5V7", "B3M0"];
      const getRand = (arr) => arr[Math.floor(Math.random() * arr.length)];
      const licenseKey = `${getRand(keys)}-${getRand(keys)}-${getRand(keys)}-${getRand(keys)}-${getRand(keys)}`;
      const isError = i % 10 === 0; // Every 10th is an error
      return isError ? {
        licenseKey,
        status: "error",
        error: {
          code: 500,
          message: "The license key invalid"
        }
      } : {
        licenseKey,
        status: "success",
        change: 2000,
        balance: 17000 + (i * 2000)
      };
    })
  }
};

export const licenseSlice = createSlice({
  name: 'license',
  initialState,
  reducers: {
    setLicenseDashboard: (state, action) => {
      state.dashboard = action.payload;
    },
    setLicenseServiceStatus: (state, action) => {
      state.serviceStatus = action.payload;
    },
    setLicenseHistory: (state, action) => {
      state.history = action.payload;
    },
    setLicenseSubscriptions: (state, action) => {
      state.subscriptions = action.payload;
    },
    updateSubscriptionStatus: (state, action) => {
      const payload = action.payload;
      let siteId, serviceKey, status;

      // Extract details from payload
      if (payload.data) {
        siteId = payload.data.siteId;
        serviceKey = payload.data.service; // e.g., "healthAI", "nmsPro"
        status = payload.data.action === "active"; // boolean
      } else {
        siteId = payload.siteId;
        serviceKey = payload.serviceId === "health-ai" ? "healthAI" : payload.serviceId === "nms-pro" ? "nmsPro" : payload.serviceId;
        status = payload.status === "Active" || payload.status === true;
      }

      // 1. Update serviceStatus (The single source of truth for mock now)
      if (siteId) {
        const site = state.serviceStatus.data.org.sites.find(s => s.id === siteId);
        if (site && site.services) {
          site.services[serviceKey] = status;
        }

        // Keep state.subscriptions in sync if it exists (legacy/fallback support)
        if (state.subscriptions.data.siteSubscriptions[siteId]) {
          const subKey = serviceKey === "healthAI" ? "health-ai" : serviceKey === "nmsPro" ? "nms-pro" : serviceKey;
          state.subscriptions.data.siteSubscriptions[siteId][subKey] = status ? "Active" : "Inactive";
        }
      }

      // 2. Recalculate Daily Usage (Daily Burn Rate) for Mock
      const baseUsage = state.serviceStatus.data.org.underMsp ? 15 : 0;
      const rates = {
        healthAI: 1,
        nmsPro: 1,
      };

      let totalDailyUsage = 0;
      state.serviceStatus.data.org.sites.forEach(site => {
        const devices = site.accessPoints + site.switches;

        // Base rate per device
        totalDailyUsage += devices * baseUsage;

        // Add service rates
        if (site.services.healthAI) totalDailyUsage += devices * rates.healthAI;
        if (site.services.nmsPro) totalDailyUsage += devices * rates.nmsPro;
      });

      // Add AI Chatbot if enabled
      if (state.serviceStatus.data.org.aiChatbotEnabled === true) totalDailyUsage += 1;

      state.dashboard.data.dailyUsage = parseFloat(totalDailyUsage.toFixed(1));
    },
  },
});

export const {
  setLicenseDashboard,
  setLicenseServiceStatus,
  setLicenseHistory,
  setLicenseSubscriptions,
  updateSubscriptionStatus
} = licenseSlice.actions;

export default licenseSlice.reducer;
